import React from 'react'
import { CookieConsumer, CookieSetter } from '#src/context/cookies'
import { DepartmentContextFragment as DepartmentContextFragmentType } from '#src/generated/graphql'
import DepartmentContextFragment from './DepartmentFragment.gql'
import { filter } from 'graphql-anywhere'

/**
 * The DepartmentProvider exposes the current department, as well as a setter.
 */
export type MaybeDepartment = DepartmentContextFragmentType | null
export type DepartmentContext = {
  department: MaybeDepartment
  setDepartment?: (department: MaybeDepartment) => void
}

// Create the underlying provider and consumer
const { Provider, Consumer } = React.createContext<DepartmentContext>({
  department: null
})

// A wrapper provider acts as the state container and manager
interface DepartmentProviderProps {
  defaultDepartment: MaybeDepartment
}

interface DepartmentProviderState {
  department: MaybeDepartment
}

class DepartmentProvider extends React.Component<DepartmentProviderProps, DepartmentProviderState> {
  public constructor(props: DepartmentProviderProps) {
    super(props)

    this.state = {
      department: this.props.defaultDepartment
    }

    this.setDepartment = this.setDepartment.bind(this)
  }

  public setDepartment(setCookie: CookieSetter, department: MaybeDepartment) {
    const filteredDepartment = filter(DepartmentContextFragment, department)
    setCookie('curated_department', JSON.stringify(filteredDepartment), {
      path: '/'
    })

    this.setState({
      department
    })
  }

  public render() {
    return (
      <CookieConsumer>
        {cookies => {
          return (
            <Provider
              value={{
                department: this.state.department,
                setDepartment: cookies ? this.setDepartment.bind(this, cookies.set) : undefined
              }}
            >
              {this.props.children}
            </Provider>
          )
        }}
      </CookieConsumer>
    )
  }
}

const DepartmentProviderInjector: React.SFC<{}> = ({ children }) => {
  return (
    <CookieConsumer>
      {cookies => {
        const departmentCookieValue = cookies && cookies.get('curated_department')
        const defaultDepartment = departmentCookieValue
          ? (JSON.parse(departmentCookieValue) as MaybeDepartment)
          : null

        return (
          <DepartmentProvider defaultDepartment={defaultDepartment}>{children}</DepartmentProvider>
        )
      }}
    </CookieConsumer>
  )
}

// Export with clean names
export { DepartmentProviderInjector as DepartmentProvider, Consumer as DepartmentConsumer }
